import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Button from './Button'
import TextInput from './TextInput'
import RefreshButton from './RefreshButton'

/**
 * Paylaşılan filtre/araç çubuğu kabuğu (013 tasarım dili — FR-010). Durum sekmeleri (sayı rozetli) +
 * feature'a özel filtre kontrolleri (children slot) + arama (PrimeReact InputText) + temizle.
 * Arama debounce'u çağıran tarafça yönetilir (Page Standards ~400ms, TR-aware). Reuse Mandate.
 */
export interface FilterTab {
  id: number | null
  label: string
  count?: number
}

export interface FilterBarProps {
  /**
   * Kök öğeye eklenecek ek sınıf. Örnek: `ui-filterbar--standalone` — altında tablo OLMAYAN
   * ekranlarda (ör. Toplantılar'ın kart ızgarası) dört köşeyi yuvarlatır; taban yalnız üst
   * köşeleri yuvarlatır çünkü normalde tabloya kaynar.
   *
   * <p>Eski örnek `ui-filterbar--flush-left` KALDIRILDI: onu kullanan ekran kalmamıştı.
   */
  className?: string
  tabs?: FilterTab[]
  activeTabId?: number | null
  onTab?: (id: number | null) => void
  /** Feature'a özel filtre kontrolleri (dropdown, radio grubu vb.). */
  children?: ReactNode
  /**
   * Filtre kontrollerinin ({@link children}) hizası. **Varsayılan `right`** — Figma tablo tasarımı
   * (180561:29596) filtreleri satırın SAĞINDA, aramayı SOLUNDA gösterir ve bu kural kullanıcı
   * kararıyla (2026-08-26) **tüm tablolar** için geçerlidir. `left` ile satırın başına alınır.
   *
   * <p>Varsayılanın çevrilmesi bilinçlidir: kural evrensel olduğu için 19 çağıran tek tek prop
   * geçmek zorunda kalmasın — yarın açılan bir ekranda o prop unutulduğunda tasarımdan sapardı.
   */
  controlsAlign?: 'left' | 'right'
  searchValue?: string
  searchPlaceholder?: string
  onSearch?: (value: string) => void
  searchAriaLabel?: string
  /**
   * Arama kutusunun **hemen sağına** çizilecek ek düğüm (2026-08-27).
   *
   * <p>`children` bu işi yapamaz: o slot `controlsAlign`'a uyar ve varsayılan olarak satırın SAĞ
   * grubuna, dropdown'ların yanına düşer — "aramanın sağı" ile "satırın sağı" farklı yerlerdir.
   * Bilet Talep Listesi'nde tam PNR/Ad-Soyad araması bu yüzden ayrı bir satırda duruyordu; kullanıcı
   * onu ana aramanın yanına istedi.
   */
  searchExtra?: ReactNode
  /**
   * Arama kutusunun hizası. **Varsayılan `left`** (satırın başı) — Figma tablo tasarımı ve kullanıcı
   * kararı 2026-08-26: arama tüm tablolarda solda. `right` ile sağ gruba alınır.
   */
  searchAlign?: 'left' | 'right'
  showClear?: boolean
  clearLabel?: string
  onClear?: () => void
  /**
   * Verilirse arama kutusunun yanında "Yenile" butonu gösterilir (021). Spinner'ı butonun kendisi
   * yönetir: dönen söz (promise) beklenir — bkz. {@link RefreshButton}. (Eski `refreshing` prop'u
   * 2026-08-31'de kaldırıldı; sorgunun `isFetching`'i sıralama/sayfalama çekimlerinde de true olduğu
   * için buton kullanıcı basmadan dönüyordu.)
   */
  onRefresh?: () => void | Promise<unknown>
}

export default function FilterBar({
  className,
  tabs,
  activeTabId,
  onTab,
  children,
  controlsAlign = 'right',
  searchValue,
  searchPlaceholder,
  onSearch,
  searchAriaLabel,
  searchExtra,
  searchAlign = 'left',
  showClear = false,
  clearLabel,
  onClear,
  onRefresh,
}: Readonly<FilterBarProps>) {
  const { t } = useTranslation()
  const searchNode = onSearch ? (
    <span className="ui-filterbar-search">
      <i className="pi pi-search" />
      <TextInput
        value={searchValue ?? ''}
        placeholder={searchPlaceholder}
        aria-label={searchAriaLabel ?? searchPlaceholder}
        onChange={(e) => onSearch(e.target.value)}
      />
      {(searchValue ?? '') !== '' && (
        <button
          type="button"
          className="ui-filterbar-search-clear"
          aria-label={t('common.clearSearch')}
          onClick={() => onSearch('')}
        >
          <i className="pi pi-times" />
        </button>
      )}
    </span>
  ) : null

  const clearNode = showClear ? (
    <Button
      type="button"
      text
      label={clearLabel}
      className="ui-filterbar-clear"
      onClick={onClear}
    />
  ) : null

  const leftAligned = searchAlign === 'left'

  return (
    <div className={`ui-filterbar${className ? ` ${className}` : ''}`}>
      {tabs && tabs.length > 0 && (
        <div className="ui-filterbar-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id ?? 'all'}
              type="button"
              role="tab"
              aria-selected={activeTabId === tab.id}
              className={`ui-filterbar-tab${activeTabId === tab.id ? ' on' : ''}`}
              onClick={() => onTab?.(tab.id)}
            >
              <span>{tab.label}</span>
              {tab.count != null && <span className="ui-filterbar-tab-c">{tab.count}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Sola hizalı arama: kutu satırın başında. */}
      {leftAligned && searchNode}
      {leftAligned && searchExtra}

      {/* Filtreler solda: Temizle onlarla BİRLİKTE solda durur. Kural "Temizle daima filtrelerin
          solunda" hizadan bağımsızdır; sağ gruba sabitlenmiş olsaydı (ilk hâl) bu modda satırın
          diğer ucunda kalırdı — Vekalet ekranında tam bu görüldü. */}
      {controlsAlign === 'left' && (
        <>
          {clearNode}
          {children}
        </>
      )}

      {/*
        Sağ grup: Yenile → "Filtreleri Temizle" → (filtreler) → Arama (sağa hizalı).
        Filtreler SOLA hizalıysa Temizle de yukarıda, onların solunda çizilir.

        Temizle, aramanın yanında DEĞİL filtrelerin SOLUNDA durur (kullanıcı isteği 2026-08-26 ve
        Figma 180561:29596): eylem filtreleri temizlemektir, dolayısıyla temizlediği kontrollerin
        yanında okunur. Önceki hâlde sola hizalı aramanın hemen sağındaydı ve satırın diğer ucundaki
        dropdown'larla ilişkisi görünmüyordu.

        Yenile, Temizle'nin de SOLUNDA: aksi hâlde ikisini birlikte veren bir ekranda (Hedef Teslim
        Tarihi Güncelleme Talepleri) Yenile araya girip Temizle'yi filtrelerden ayırıyordu. Sıra
        burada sabitlenince kural ekran bazında değil YAPISAL olarak korunur — yeni bir ekran ikisini
        birden verse de Temizle filtrelere komşu kalır.

        Yalnız arama taşıyan ekranlar bu butonu HİÇ vermez (`showClear` geçmezler): aramanın kendi
        `×` işareti aynı işi yapar ve ikinci bir kontrol gereksiz tekrar olurdu.
      */}
      <div className="ui-filterbar-right">
        {onRefresh && <RefreshButton onRefresh={onRefresh} />}
        {controlsAlign === 'right' && clearNode}
        {controlsAlign === 'right' && children}
        {!leftAligned && searchNode}
        {!leftAligned && searchExtra}
      </div>
    </div>
  )
}
